import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import { canAccessPlanning } from '../../../Routegaurd/PlanningRouteGuard';
import { getTimeOffsForManager } from "../../../services/userServices/UserInfoService";
import { ROLES } from "../../../Data/SD";
import Feedback from "../../feedback/Feedback";
import { AppEvents } from "../../../events/events";
import { hasPolicyPermissionV2 } from "../../../utilities/common/policyFuntions";
import { POLICYPERMISSIONS } from "../../../Data/PolicyPermissions";
import { getPendingSignatureInboxCount } from "../../../services/fileServices/signatures/SignatureService";
import { getOwnOpenEmployeeTaskCount } from "../../../services/taskServices/TaskService";
import {trainingRequest} from '../../../services/training/TrainingService';
import {subscribeTrainingAvailabilityDay} from '../../../utilities/notifications/trainingWorkEvents';
import {useResourceReviewCount} from '../../components/resourceWorkflow/ResourceReviewAlert';

// Import Custom Tooltip

// Lucide Icons

import { LayoutDashboard, ClipboardList, ListTodo, Users, HardDrive, ShieldAlert, UsersRound, Bug, Ticket, BookOpen, Search, MessageSquareWarning, LogOut, ChevronLeft, ChevronRight, Zap, PieChart, TrendingUp, BookUser, FileSignature, FileInput } from "lucide-react";
import { WaiveraLogo } from "../../ui/Logo/WaiveraLogo";
import Tooltip from "../../ui/Tooltip/Tooltip";
import { WaiveraLogoLetter } from "../../ui/Logo/WaiveraLogoLetter";
import "./SideBarcomponent.css";

interface SideBarComponentProps {
    isShowSidebarMobile?: boolean;
    onToggleCollapse?: (isCollapsed: boolean) => void;
}

// Reusable NavItem Component adapted for Tooltips
const NavItem = ({
    icon: Icon,
    label,
    isActive,
    onClick,
    badgeCount,
    badgeLabel,
    isCollapsed,
}: {
    icon: React.ElementType;
    label: string;
    isActive: boolean;
    onClick: () => void;
    badgeCount?: number;
    badgeLabel?: string;
    isCollapsed: boolean;
}) => {
    const innerContent = (
        <div
            className={`sidebar-nav-item ${isCollapsed ? 'sidebar-nav-item--collapsed justify-center p-2.5' : 'gap-2 px-3 py-2'} flex items-center rounded-xl font-medium text-sm transition-all duration-200 cursor-pointer ${isActive
                ? "bg-[#7E57C2]/10 dark:bg-purple-400/15 text-[#7E57C2] dark:text-purple-300"
                : "text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white"
                }`}
        >
            <div className="relative flex items-center justify-center shrink-0">
                <Icon
                    size={isCollapsed ? 20 : 16}
                    className={`sidebar-nav-icon transition-colors ${isActive ? "text-[#7E57C2] dark:text-purple-300" : "text-gray-400 dark:text-slate-500 group-hover:text-gray-600 dark:group-hover:text-slate-300"}`}
                />

                {/* Micro-badge for collapsed state */}
                {isCollapsed && badgeCount !== undefined && badgeCount > 0 && (
                    <span aria-label={badgeLabel} className="absolute -top-2 -right-2 bg-[#FF9E9E] text-gray-900 text-[9px] font-semibold px-1 rounded-full min-w-[16px] h-[16px] flex items-center justify-center shadow-sm">
                        {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                )}
            </div>

            {!isCollapsed && (
                <>
                    <span className="truncate">{label}</span>
                    {/* Standard badge for expanded state */}
                    {badgeCount !== undefined && badgeCount > 0 && (
                        <Tooltip content={badgeLabel || label} position="right"><span aria-label={badgeLabel} className="ml-auto bg-[#FF9E9E] text-gray-900 text-[10px] font-semibold px-1.5 py-0.5 rounded-md min-w-[1.25rem] text-center">
                            {badgeCount}
                        </span></Tooltip>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div onClick={onClick} className={`flex-shrink-0 w-full ${isCollapsed && "flex justify-center"}`}>
            {isCollapsed ? (
                <Tooltip content={label} position="right" theme="dark">
                    {innerContent}
                </Tooltip>
            ) : (
                innerContent
            )}
        </div>
    );
};

export default function SideBarComponent({
    isShowSidebarMobile,
    onToggleCollapse
}: SideBarComponentProps) {
    const {
        currentRoles,
        currentUser,
        currentUserInfo,
        currentOrganizationRolePolicies,
        currentOrganizationRoles,
        userAddedInRolePolicy,
        impersonatedUserId,
        currentOrganization,
        hasPermission,
    } = useAuth();

    const location = useLocation();
    const navigate = useNavigate();

    const [showFeedback, setShowFeedback] = useState(false);
    const [adminRequestsCount, setAdminRequestsCount] = useState<number>(0);
    const [signatureRequestsCount, setSignatureRequestsCount] = useState<number>(0);
    const userId = impersonatedUserId || currentUser?.uid;
    const authenticatedUserId = currentUser?.uid;
    const organizationIdentity = `${currentUserInfo?.organization.id ?? ""}:${currentOrganization?.abbreviation ?? ''}:${currentOrganization?.name ?? ''}`;
    const [employeeTaskBadge, setEmployeeTaskBadge] = useState<{count: number; user: typeof currentUser; userId: typeof userId; authenticatedUserId: typeof authenticatedUserId; organization: typeof currentOrganization; organizationIdentity: string; impersonatedUserId: typeof impersonatedUserId} | undefined>();
    const canUseTaskWorkspace = Boolean(currentRoles && !currentRoles.some(role => ["DEVELOPER", "SUPER ADMIN"].includes(role.name)));
    useEffect(() => {
        if (!currentUser || !canUseTaskWorkspace || impersonatedUserId) return;
        return subscribeTrainingAvailabilityDay(() => AppEvents.emit('UPDATE_EMPLOYEE_TASK_COUNT'));
    }, [currentUser, canUseTaskWorkspace, impersonatedUserId]);
    // A known count belongs only to this authentication/organization/impersonation context.
    // Keep it visible during route refreshes, but hide it synchronously on a context change.
    const employeeTaskCount = canUseTaskWorkspace && employeeTaskBadge?.user === currentUser && employeeTaskBadge?.userId === userId && employeeTaskBadge?.authenticatedUserId === authenticatedUserId && employeeTaskBadge?.organization === currentOrganization && employeeTaskBadge?.organizationIdentity === organizationIdentity && employeeTaskBadge?.impersonatedUserId === impersonatedUserId ? employeeTaskBadge?.count : undefined;
    useEffect(() => {
        let controller: AbortController | undefined;
        const refreshCount = async () => {
            controller?.abort();
            const request = new AbortController();
            controller = request;
            if (!currentUser || !userId || !canUseTaskWorkspace) return;
            try {
                const token = await currentUser.getIdToken();
                if (request.signal.aborted) return;
                const count = await getOwnOpenEmployeeTaskCount(token, userId, request.signal);
                if (!request.signal.aborted) setEmployeeTaskBadge({count, user: currentUser, userId, authenticatedUserId, organization: currentOrganization, organizationIdentity, impersonatedUserId});
            } catch { /* Keep a known same-context count on transient errors; never invent zero. */ }
        };
        void refreshCount();
        const unsubscribe = AppEvents.subscribe("UPDATE_EMPLOYEE_TASK_COUNT", refreshCount);
        const unsubscribeApproval = AppEvents.subscribe("UPDATE_ADMIN_REQUEST_COUNT", refreshCount);
        window.addEventListener("focus", refreshCount);
        return () => {
            controller?.abort();
            unsubscribe();
            unsubscribeApproval();
            window.removeEventListener("focus", refreshCount);
        };
    }, [currentUser, authenticatedUserId, userId, impersonatedUserId, currentOrganization, organizationIdentity, canUseTaskWorkspace, location.pathname]);
    const [trainingBadge,setTrainingBadge]=useState<{count:number;identity:string}>();
    const trainingIdentity=[authenticatedUserId,currentUserInfo?.id,currentUserInfo?.organization.id,impersonatedUserId].join(':');
    const trainingCount=trainingBadge?.identity===trainingIdentity?trainingBadge.count:undefined;
    useEffect(()=>{let live=true;let controller:AbortController|undefined;const refresh=async()=>{controller?.abort();controller=new AbortController();const request=controller;if(!currentUser||!canUseTaskWorkspace||impersonatedUserId){setTrainingBadge(undefined);return;}try{const token=await currentUser.getIdToken();if(request.signal.aborted)return;const result=await trainingRequest<{count:number}>(token,'actionable','GET',undefined,request.signal);if(live&&!request.signal.aborted)setTrainingBadge({identity:trainingIdentity,count:result.count});}catch{/* Retain only same-account evidence on transient failures. */}};void refresh();const task=AppEvents.subscribe('UPDATE_EMPLOYEE_TASK_COUNT',refresh);const admin=AppEvents.subscribe('UPDATE_ADMIN_REQUEST_COUNT',refresh);window.addEventListener('focus',refresh);return()=>{live=false;controller?.abort();task();admin();window.removeEventListener('focus',refresh);};},[currentUser,trainingIdentity,canUseTaskWorkspace,impersonatedUserId,location.pathname]);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        const savedState = localStorage.getItem("sidebarCollapsed");
        return savedState ? JSON.parse(savedState) : true;
    });

    const handleToggleCollapse = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        if (onToggleCollapse) {
            onToggleCollapse(newState);
        }
    };



    useEffect(() => {
const refreshCount = async () => {
        if (!currentUser || !currentRoles) return;

        // If impersonating, we use a softer check, or we ensure the impersonated role's permissions are used
        const hasAccess = currentRoles.some(
            (role) =>
                role.name === ROLES.OWNER ||
                role.name === "ADMIN" ||
                role.name === "PROGRAM_MANAGER" ||
                role.name === "SUPERVISOR"
        ) || currentOrganizationRoles?.some((role) => role.systemKey === "ORG_OWNER") || localStorage.getItem('impersonatedUserId');

        if (!hasAccess) {
            setAdminRequestsCount(0);
            return;
        }

        try {
            const token = await currentUser.getIdToken();
            const userId = currentUser.uid;

            const result = await getTimeOffsForManager(userId, token, "", 1, 1, "Pending", undefined, undefined);

            if (result.success && result.data) {
                setAdminRequestsCount(result.data.totalCount || 0);
            } else {
                setAdminRequestsCount(0);
            }
        } catch (error) {
            console.error("Error fetching admin requests count:", error);
        }
    };

        refreshCount();
        const unsubscribe = AppEvents.subscribe("UPDATE_ADMIN_REQUEST_COUNT", refreshCount);
        return unsubscribe;
    }, [currentUser, currentRoles, currentOrganizationRoles]);

    useEffect(() => {
        const refreshSignatureCount = async () => {
            if (!currentUser) {
                setSignatureRequestsCount(0);
                return;
            }
            try {
                setSignatureRequestsCount(await getPendingSignatureInboxCount(await currentUser.getIdToken()));
            } catch (error) {
                console.error("Error fetching signature request count:", error);
            }
        };
        refreshSignatureCount();
        const unsubscribe = AppEvents.subscribe("UPDATE_SIGNATURE_REQUEST_COUNT", refreshSignatureCount);
        const expiryRefresh = window.setInterval(refreshSignatureCount, 60_000);
        return () => {
            unsubscribe();
            window.clearInterval(expiryRefresh);
        };
    }, [currentUser]);

    // Team management has no legacy fallback on the API: organization owners, or the
    // team:manage policy. Legacy-only accounts no longer see it (decided 2026-11-03).
    const canViewTeam = !!currentOrganizationRoles?.some(role => role.systemKey === "ORG_OWNER") ||
        !!currentRoles?.some(role => role.name === "OWNER") ||
        hasPolicyPermissionV2(currentOrganizationRolePolicies, POLICYPERMISSIONS.TeamPolicies.View.key, userAddedInRolePolicy, undefined, false);
    const canViewReports = hasPolicyPermissionV2(currentOrganizationRolePolicies, POLICYPERMISSIONS.ReportPolicies.View.key, userAddedInRolePolicy);
    const canUseAgencyReports = !impersonatedUserId && (!!currentOrganizationRoles?.some(role => role.systemKey === "ORG_OWNER") ||
        ["report:read", "report:generate"].some(key => hasPolicyPermissionV2(currentOrganizationRolePolicies, key, userAddedInRolePolicy, undefined, false)));
    const canViewAnalytics = hasPolicyPermissionV2(currentOrganizationRolePolicies, POLICYPERMISSIONS.AnalyticsPolicies.View.key, userAddedInRolePolicy);
    const canViewSignatureAndFileRequests = hasPolicyPermissionV2(
        currentOrganizationRolePolicies,
        POLICYPERMISSIONS.DashboardWidgetPolicies.View.key,
        userAddedInRolePolicy,
        POLICYPERMISSIONS.DashboardWidgetPolicies.View.scope.Count_CIR_Resource_Request_Sign_File_Review,
    );

    const isSuperAdmin = currentRoles && currentRoles.some((role) => role.name === ROLES.SUPER_ADMIN);
    const resourceReviewCount=useResourceReviewCount(!!isSuperAdmin&&!impersonatedUserId);
    const isNotOwnerAdminDevOrSuper = currentRoles && !currentRoles.some((role) => role.name === ROLES.OWNER || role.name === "ADMIN" || role.name === "DEVELOPER" || role.name === "SUPER ADMIN") && !currentOrganizationRoles?.some((role) => role.systemKey === "ORG_OWNER" || role.name === "Owner");
    const isNotDevOrSuper = currentRoles && !currentRoles.some((role) => role.name === "DEVELOPER" || role.name === "SUPER ADMIN");

    // Reusable structural components for the bottom buttons to keep it DRY with tooltips
    const feedbackButtonContent = (
        <button
            className={`sidebar-bottom-action flex items-center ${isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-2 py-2'} w-full text-left font-medium text-sm text-gray-600 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors border-none cursor-pointer group bg-transparent`}
            onClick={() => setShowFeedback(true)}
        >
            <MessageSquareWarning size={isCollapsed ? 20 : 16} className="text-gray-400 group-hover:text-gray-600 transition-colors shrink-0" />
            {!isCollapsed && <span>Report a Bug</span>}
        </button>
    );

    const logoutButtonContent = (
        <button
            className={`sidebar-bottom-action flex items-center ${isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-2 py-2'} w-full text-left font-medium text-sm text-red-500 rounded-xl hover:bg-red-50 transition-colors border-none cursor-pointer group bg-transparent`}
            onClick={() => navigate("/logout")}
        >
            <LogOut size={isCollapsed ? 20 : 16} className="text-red-400 group-hover:text-red-600 transition-colors shrink-0" />
            {!isCollapsed && <span>Sign Out</span>}
        </button>
    );

    const logoContent = (
        <Link to="/home" className="block outline-none">
            {isCollapsed ? (
                <div className="w-9 h-9 text-[#7E57C2] rounded-xl flex items-center justify-center font-semibold text-xl">
                    <WaiveraLogoLetter />
                </div>
            ) : (
                <WaiveraLogo />
            )}
        </Link>
    );

    return (
        <>
            {/* 1. The Outer Wrapper (No overflow clipping, handles the width and shadow) */}
            <aside
                className={`app-sidebar ${isCollapsed ? "app-sidebar--collapsed" : "app-sidebar--expanded"} dark-surface fixed top-0 left-0 z-[500] h-[100dvh] bg-white dark:bg-zinc-900 transform transition-all duration-300 ease-in-out border-r border-gray-100 dark:border-white/5 shadow-sm dark:shadow-none
                ${isShowSidebarMobile ? "translate-x-0 shadow-2xl" : "-translate-x-full xl:translate-x-0"}`}
            >
                {/* Floating Chevron Toggle Button (Escapes clipping because it is outside the overflow wrapper!) */}
                <button
                    onClick={handleToggleCollapse}
                    className="sidebar-collapse-toggle dark-surface-muted dark-border-subtle hidden xl:flex absolute -right-3 top-8 w-6 h-6 bg-white dark:bg-zinc-800 border border-gray-200 rounded-full items-center justify-center shadow-md text-gray-400 dark:text-zinc-400 hover:text-[#7E57C2] hover:border-[#7E57C2] transition-colors z-[99] cursor-pointer"
                >
                    {isCollapsed ? <ChevronRight size={14} strokeWidth={3} /> : <ChevronLeft size={14} strokeWidth={3} />}
                </button>

                {/* 2. The Inner Scrollable Area */}
                <div className={`app-sidebar-scroll theme-scrollbar flex flex-col h-full w-full overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent py-4 xl:py-5 ${isCollapsed ? "px-2.5" : "px-3"}`}>

                    {/* Logo Section */}
                    <div className={`app-sidebar-logo flex-shrink-0 mb-8 flex ${isCollapsed ? 'justify-center' : 'px-2'}`}>
                        {isCollapsed ? (
                            <Tooltip content="Waivera Home" position="right" theme="dark">
                                {logoContent}
                            </Tooltip>
                        ) : (
                            logoContent
                        )}
                    </div>

                    {/* Navigation Links */}
                    <div className="app-sidebar-nav flex flex-col gap-1 flex-1">
                        {isSuperAdmin ? (
                            <>
                                <NavItem icon={LayoutDashboard} label="Dashboard" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/dashboard/superadmin")} onClick={() => navigate("/dashboard/superadmin")} />
                                {/* <NavItem icon={Form} label="Forms" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/forms/v2")} onClick={() => navigate("/forms/v2/")} /> */}
                                {/* <NavItem icon={CreditCard} label="Plans" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/superadmin/plans")} onClick={() => navigate("/superadmin/plans")} /> */}
                                <NavItem icon={Search} label="Resources" badgeCount={resourceReviewCount} badgeLabel="Resource suggestions waiting for review" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/resources")} onClick={() => navigate(resourceReviewCount ? "/resources?tab=manage#resource-review" : "/resources")} />
                                <NavItem icon={Bug} label="Issues" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/issue")} onClick={() => navigate("/issues")} />
                                <NavItem icon={Zap} label="Epics" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/epics")} onClick={() => navigate("/epics")} />
                                <NavItem icon={Ticket} label="Sprints" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/sprints")} onClick={() => navigate("/sprints")} />
                            </>
                        ) : isNotOwnerAdminDevOrSuper ? (
                            <>
                                <NavItem icon={LayoutDashboard} label="Dashboard" isCollapsed={isCollapsed} isActive={location.pathname === "/dashboard" || location.pathname.startsWith("/home") || location.pathname.endsWith("/auth2/dashboard")} onClick={() => navigate("/dashboard")} />
                                <NavItem icon={ListTodo} label="Tasks" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/tasks") || location.pathname.startsWith("/task")} onClick={() => navigate("/tasks")} />
                                <NavItem icon={ClipboardList} label="Employee Tasks" badgeCount={employeeTaskCount} badgeLabel="Your open employee tasks" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/employee-tasks")} onClick={() => navigate("/employee-tasks")} />
                                {currentRoles?.some((role) => role.name === ROLES.PROGRAM_MANAGER) && (
                                    <NavItem icon={Users} label="Participants" isCollapsed={isCollapsed} isActive={location.pathname.includes("/participants")} onClick={() => navigate("/participants")} />
                                )}
                                <NavItem icon={HardDrive} label="Drive" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/drive")} onClick={() => navigate("/drive/caseFileDrive")} />
                                <NavItem icon={Search} label="Resources" badgeCount={resourceReviewCount} badgeLabel="Resource suggestions waiting for review" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/resources")} onClick={() => navigate(resourceReviewCount ? "/resources?tab=manage#resource-review" : "/resources")} />
                                <NavItem icon={BookOpen} label="Training" badgeCount={trainingCount} badgeLabel="Required training needing your action" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/training")} onClick={() => navigate("/training")} />
                                <NavItem icon={FileSignature} label="Signatures" badgeCount={signatureRequestsCount} isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/signatures")} onClick={() => navigate("/signatures")} />
                                {canViewSignatureAndFileRequests && (
                                    <>
                                        <NavItem icon={FileInput} label="File Requests" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/external-file-request")} onClick={() => navigate("/external-file-request")} />
                                    </>
                                )}

                                {/* New Dynamic Policy Items for Regular Users */}
                                {canViewTeam && <NavItem icon={UsersRound} label="Team" isCollapsed={isCollapsed} isActive={location.pathname.includes("/dashboard/owner/team")} onClick={() => navigate("/dashboard/owner/team")} />}
                                {(canViewReports || canUseAgencyReports) && <NavItem icon={PieChart} label="Reports" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/owner/reports") || location.pathname.startsWith("/reports/agency")} onClick={() => navigate(canUseAgencyReports ? "/reports/agency" : "/owner/reports")} />}
                                {canViewAnalytics && <NavItem icon={TrendingUp} label="Analytics" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/owner/analytics")} onClick={() => navigate("/owner/analytics")} />}

                                {currentOrganizationRoles && (!currentOrganizationRoles.some((role) => role.name === "Office Manager") || hasPermission?.("admin_request:view:external_training") || hasPermission?.("admin_request:approve:external_training")) && (
                                    <NavItem icon={ShieldAlert} label="Admin Requests" badgeCount={adminRequestsCount} isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/admin-requests")} onClick={() => navigate("/admin-requests")} />
                                )}
                            </>
                        ) : isNotDevOrSuper ? (
                            <>
                                <NavItem icon={LayoutDashboard} label="Dashboard" isCollapsed={isCollapsed} isActive={location.pathname.endsWith("/auth2/dashboard") || location.pathname === "/dashboard" || location.pathname.endsWith("/dashboard/owner")} onClick={() => navigate("/dashboard")} />
                                <NavItem icon={ListTodo} label="Tasks" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/tasks") || location.pathname.startsWith("/task")} onClick={() => navigate("/tasks")} />
                                <NavItem icon={ClipboardList} label="Employee Tasks" badgeCount={employeeTaskCount} badgeLabel="Your open employee tasks" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/employee-tasks")} onClick={() => navigate("/employee-tasks")} />
                                <NavItem icon={Users} label="Participants" isCollapsed={isCollapsed} isActive={location.pathname.includes("/participants")} onClick={() => navigate("/participants")} />
                                <NavItem icon={HardDrive} label="Drive" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/drive") || location.pathname.includes("/drive/caseFileDrive")} onClick={() => navigate("/drive/caseFileDrive")} />

                                {/* Replaced Hardcoded Team with Dynamic Policy Items */}
                                {canViewTeam && <NavItem icon={BookUser} label="Team" isCollapsed={isCollapsed} isActive={location.pathname.includes("/dashboard/owner/team")} onClick={() => navigate("/dashboard/owner/team")} />}
                                {canViewReports && <NavItem icon={PieChart} label="Reports" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/owner/reports")} onClick={() => navigate("/owner/reports")} />}
                                {canViewAnalytics && <NavItem icon={TrendingUp} label="Analytics" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/owner/analytics")} onClick={() => navigate("/owner/analytics")} />}
                                <NavItem icon={Search} label="Resources" badgeCount={resourceReviewCount} badgeLabel="Resource suggestions waiting for review" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/resources")} onClick={() => navigate(resourceReviewCount ? "/resources?tab=manage#resource-review" : "/resources")} />
                                <NavItem icon={BookOpen} label="Training" badgeCount={trainingCount} badgeLabel="Required training needing your action" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/training")} onClick={() => navigate("/training")} />
                                <NavItem icon={FileSignature} label="Signatures" badgeCount={signatureRequestsCount} isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/signatures")} onClick={() => navigate("/signatures")} />
                                <NavItem icon={FileInput} label="File Requests" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/external-file-request")} onClick={() => navigate("/external-file-request")} />
                                <NavItem icon={ShieldAlert} label="Admin Requests" badgeCount={adminRequestsCount} isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/admin-requests")} onClick={() => navigate("/admin-requests")} />
                            </>
                        ) : (
                            <>
                                {canAccessPlanning(currentRoles) && (
                                    <>
                                    <NavItem icon={Bug} label="Issues" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/issue")} onClick={() => navigate("/issues")} />
                                    <NavItem icon={Zap} label="Epics" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/epics")} onClick={() => navigate("/epics")} />
                                    <NavItem icon={Ticket} label="Sprints" isCollapsed={isCollapsed} isActive={location.pathname.startsWith("/sprints")} onClick={() => navigate("/sprints")} />
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    {/* Bottom Section - Feedback, Logout */}
                    <div className="app-sidebar-bottom mt-auto pt-4 flex flex-col gap-1 shrink-0">
                        {isCollapsed ? (
                            <Tooltip content="Report a Bug" position="right" theme="dark">
                                {feedbackButtonContent}
                            </Tooltip>
                        ) : (
                            feedbackButtonContent
                        )}

                        <div className="h-px bg-gray-100 dark:bg-slate-800 my-1 mx-2" />

                        {isCollapsed ? (
                            <Tooltip content="Sign Out" position="right" theme="dark">
                                {logoutButtonContent}
                            </Tooltip>
                        ) : (
                            logoutButtonContent
                        )}
                    </div>
                </div>
            </aside>

            <Feedback isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
        </>
    );
}
